import {ButtonWithIcon, LabelWithoutJson} from '@/components/atoms'
import {AccordionItemCustom as AccordionItem} from '@/components/molecules/AccordionItemCustom'
import {PromotionStep, RewardType} from '@/types/promotion'
import {COLOR, COLOR_CODE} from '@/utils/constants/colors'
import {FONT_SIZE, FONT_WEIGHT, SIZE} from '@/utils/constants/globalStyle'
import {formatDateToMMDDYYYY} from '@/helpers/shadcn/utils'
import {PromotionType} from '@/utils/constants/promotionConstants'

const PromotionsReviewAttributes = ({promotionReviewData, isDetailView, handleEditStep}) => {
  const LabelWithVal = ({keyName, val}) => {
    const id = keyName?.toLowerCase()?.replaceAll(' ', '_') ?? ''
    return (
      <div className="col-4 overflow-hidden pt-5">
        <div>
          <LabelWithoutJson
            id={`${id}_label`}
            text={keyName}
            fontWeight={FONT_WEIGHT.NORMAL}
            fontSize={FONT_SIZE.XS}
            className="font-body"
          />
          <LabelWithoutJson
            id={`${id}_value`}
            text={val}
            isValueDiasbledInput={false}
            fontWeight={FONT_WEIGHT.BOLD}
            fontSize={FONT_SIZE.SM}
            className="font-body"
            color={COLOR_CODE._FFFFFF}
          />
        </div>
      </div>
    )
  }

  const getDateRange = () => {
    if (promotionReviewData.activeStartDate && promotionReviewData.activeEndDate) {
      const startDate = formatDateToMMDDYYYY(promotionReviewData.activeStartDate)
      const endDate = formatDateToMMDDYYYY(promotionReviewData.activeEndDate)
      return `${startDate} - ${endDate}`
    }
    return '-'
  }

  const getResetFrequency = () => {
    return promotionReviewData.resetFrequencyInDays ? `${promotionReviewData.resetFrequencyInDays} Days` : '-'
  }

  // Determine reward type once so it can be reused throughout the component
  const rewardType = promotionReviewData?.promotionConditions?.[0]?.rewards?.[0]?.type
  const promotionType = promotionReviewData?.promotionType

  // Function to render additional attributes based on promotion type
  const renderAdditionalAttributes = () => {
    switch (rewardType) {
      case RewardType.FREE_PRODUCT:
      case RewardType.GIFT_ITEM:
        return (
          <div className="overflow-hidden">
            <LabelWithVal
              keyName="Max Free Items per Order"
              val={promotionReviewData.maxFreeItemsPerOrder?.toString() || '-'}
            />
          </div>
        )
      case RewardType.POINTS:
        return (
          <div className="overflow-hidden">
            <LabelWithVal keyName="Max Times per Order" val={promotionReviewData.maxTimesPerOrder?.toString() || '-'} />
          </div>
        )
      default:
        return (
          <div className="overflow-hidden">
            <LabelWithVal
              keyName="Max Allowed Percentage"
              val={promotionReviewData.maxAllowedPercentage?.toString() || '-'}
            />
          </div>
        )
    }
  }

  return (
    <AccordionItem
      isDark
      size="large"
      className="min-h-12 w-full gap-2 rounded p-4"
      labelClassName="font-body text-base font-medium leading-normal"
      label={
        <div className="flex w-full items-center justify-between">
          <div className="font-medium">Attributes</div>
        </div>
      }
      rightAccessoryChild={
        !isDetailView ? (
          <div
            className="ml-4"
            onClick={e => e.stopPropagation()}
            role="presentation"
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation()
              }
            }}
          >
            <ButtonWithIcon
              iconLeading="edit"
              handleButton={() => handleEditStep(PromotionStep.ATTRIBUTES)}
              uiSchema={{
                className: 'flex items-center text-sm text-[#0069D7] border-none',
                title: 'Edit',
                size: SIZE.SMALL,
                variant: COLOR.TERTIARY,
                isDark: true,
              }}
            />
          </div>
        ) : null
      }
      presentation="test"
      content={
        <div
          className="promotions-content-wrapper w-full overflow-hidden"
          style={{padding: '12px 16px', gap: '16px', minWidth: '1238px'}}
        >
          {/* Availability Section */}
          <div className="w-full overflow-hidden">
            <div className="sc-border heading pb-5 text-base font-bold">Availability</div>
            <div className="grid w-full grid-cols-3 gap-6 overflow-hidden pb-5">
              <div className="overflow-hidden">
                <LabelWithVal keyName="Date" val={getDateRange()} />
              </div>
              <div className="overflow-hidden">
                <LabelWithVal keyName="Active" val={promotionReviewData.active ? 'Yes' : 'No'} />
              </div>
            </div>
          </div>

          {/* Additional Attributes Section */}
          <div className="mt-6 w-full overflow-hidden">
            <div className="sc-border heading pb-5 text-base font-bold">Additional Attributes</div>
            <div className="grid w-full grid-cols-3 gap-6 overflow-hidden pb-5">
              {renderAdditionalAttributes()}
              <div className="overflow-hidden">
                <LabelWithVal
                  keyName="Max Allowed Order per Store"
                  val={promotionReviewData.maxAllowedOrderPerStore?.toString() || '-'}
                />
              </div>
              {promotionType !== PromotionType.WELCOME_DISCOUNT && (
                <div className="overflow-hidden">
                  <LabelWithVal keyName="Reset Frequency" val={getResetFrequency()} />
                </div>
              )}
            </div>
            {promotionType !== PromotionType.WELCOME_DISCOUNT && (
              <div className="grid w-full grid-cols-3 gap-6 overflow-hidden pb-5">
                <div className="overflow-hidden">
                  <LabelWithVal keyName="Behavior" val={promotionReviewData.behavior || '-'} />
                </div>
              </div>
            )}
          </div>
        </div>
      }
    />
  )
}

export default PromotionsReviewAttributes
